import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Col, Row } from "react-styled-flexboxgrid"

import useFetchIncidents from "./hooks/useFetchIncidents";
import { TStepProps, TUseLocation } from "./types";

import Box from "../../Global/Atomics/Box"
import { Button } from "../../Global/Atomics/Button";
import LoadingNova from "../../Global/Atomics/LoadingNova";
import { Paragraph } from "../../Global/Atomics/Paragraph"
import * as IconsLibrary from '../../Global/Icons/Library';
import CategoryBox from "../../Global/Widgets/CategoryBox/CategoryBox"
import IncidentCard from "../../Global/Widgets/Cards/IncidentCard";

import { useGlobalStateContext } from "../../../contexts/GlobalStateContext";
import { useReportFormContext } from "../../../contexts/ReportFormContext";
import { mappedCategoriesBySection } from "../../../utils/constants/categories";

type TProps = TStepProps & {
    translate: (name: string) => string;
}

const Step1 = ({ step, setStep, resources, translate }: TProps) => {
    const { state: { section }, } = useLocation() as TUseLocation;
    const { globalState, setGlobalState } = useGlobalStateContext();
    const [showList, setShowList] = useState(globalState.showList ?? false);
    const [selectedCategory, setSelectedCategory] = useState({ icon: '', name: '', id: '' });
    const { updateForm } = useReportFormContext();
    const { data, isLoading, isFetching } = useFetchIncidents({ selectedCategoryId: selectedCategory.id, step, setStep, showList, setShowList }); // API Call

    let incidentList = globalState.incidentList ? globalState.incidentList : data;

    // Show categories inside selected section
    const categories = mappedCategoriesBySection(section);

    const handleCategoryClick = (icon: IconsLibrary.LibraryTypes, name: string, id: string) => {
        setSelectedCategory({ icon, name, id });

        // If user returns to Step 1 and selectes a different category, the form should be reset
        if (id !== globalState.selectedBusinessId) {
            updateForm({ reset: true });
        }
    }

    if (isLoading || isFetching) {
        return (
            <Box mt="lg" mb="sm" pb="xs">
                <LoadingNova description={resources.report.step1.checkingReportedIncidents} />
            </Box>
        )
    }

    if (showList) {
        return (
            <>
                <Box mt="lg" mb="sm" pb="xs">
                    <Paragraph typography="text-s-bold" customColor="black-1">{resources.report.step1.title2}</Paragraph>
                </Box>

                <Row>
                    {incidentList?.map((incident, id: number) => {
                        return (
                            <Col xs={12} md={4} key={id}>
                                <IncidentCard
                                    categoryTitle={incident.categoryTitle}
                                    incidentId={incident.incidentId}
                                    incidentTitle={incident.incidentTitle}
                                    incidentDescription={incident.incidentDescription}
                                    section={section}
                                    lat={incident.lat}
                                    lng={incident.lng}
                                    statusLevel={incident.statusLevel}
                                    date={incident.date}
                                    watchListId={incident.watchListId}
                                    openedBy={incident.openedBy}
                                    callerId={incident.callerId}
                                    icon={incident.icon}
                                />
                            </Col>
                        )
                    })}
                </Row>

                <Row center="xs">
                    <Col xs={12} md={4}>
                        <Box mt="lg" width="100%">
                            <Button
                                size="s"
                                width="100%"
                                variant="primary-black"
                                onClick={() => { setStep(step + 1); setGlobalState({ id: 'showList', value: false }) }}
                            >
                                {resources.report.step1.ctaText}
                            </Button>
                        </Box>
                    </Col>
                </Row>
            </>
        )
    }

    return (
        <>
            <Box mt="lg" mb="sm" pb="xs">
                <Paragraph typography="text-s-bold" customColor="black-1">{resources.report.step1.title1}</Paragraph>
            </Box>

            <Row>
                {categories.map(({ icon, name, id }, idx) => {
                    return (
                        <Col xs={6} md={4} key={idx}>
                            <CategoryBox
                                name={translate(name)}
                                icon={icon as IconsLibrary.LibraryTypes}
                                onClick={() => handleCategoryClick(icon as IconsLibrary.LibraryTypes, name, id)}
                            />
                        </Col>
                    )
                })}
            </Row>
        </>
    )
}

export default React.memo(Step1)